import { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { mdyDateFormat } from 'lib/dateUtils'
import { getSearchItemFromURLSearchParams } from 'lib/urlUtils'
import moment from 'moment'

function formatTravellerType(key: string): 'ADT' | 'CHD' {
  const type = key.split('-')[0].toLowerCase()
  return (type === 'adult') ? 'ADT' : 'CHD'
}

export const titleGenderMap = {
  Sr: 'M',
  Mstr: 'M',
  Mr: 'M',
  Ms: 'F',
  Mrs: 'F',
  Miss: 'F',
}

function getTitleGender(personTitles: App.CruisePersonTitle[] | null) {
  const personTitleGenders = (personTitles ?? []).reduce((acc, curr) => {
    acc[curr.code] = titleGenderMap[curr.code] ?? 'M'
    return acc
  }, {} as { [key: string]: string })

  return personTitles ? personTitleGenders : titleGenderMap
}

export function getCruiseDurationByItinerary(itinerary?: App.CruiseItineraryItem[]): number {
  const uniqueItineraryDays = new Set<number>()
  itinerary?.forEach(item => uniqueItineraryDays.add(item.startDay))

  return uniqueItineraryDays.size
}

export function getCruiseCountriesByItinerary(itinerary?: App.CruiseItineraryItem[]): number {
  const countries = (itinerary ?? []).map(({ port }) => port?.countryCode).filter(Boolean)
  return new Set(countries).size
}

export function getCruisePortsByItinerary(itinerary?: App.CruiseItineraryItem[]): number {
  const cities = (itinerary ?? []).map(({ port }) => {
    if (!port) return
    return port.city || port.name.split(',')[0]
  }).filter(Boolean)
  return new Set(cities).size
}

export function formatTraveller(travellerForms: App.CheckoutTravellerForm[], personTitles: App.CruisePersonTitle[] | null) {
  const titleGenderMap = getTitleGender(personTitles)

  let traveller = {}
  const phoneNumber = travellerForms[0].phonePrefix ?? `${travellerForms[0].prefix}${travellerForms[0].phone}`
  const emailAddress = travellerForms[0].email
  travellerForms.forEach((travellerForm, index) => {
    traveller = {
      ...traveller,
      [index + 1]: {
        type: formatTravellerType(travellerForm.id),
        gender: titleGenderMap[travellerForm.title],
        firstName: travellerForm.firstName,
        middleName: travellerForm.middleName,
        lastName: travellerForm.lastName,
        dateOfBirth: mdyDateFormat(new Date(travellerForm.dateOfBirth)),
        citizenshipCountry: travellerForm.countryOfResidence,
        title: travellerForm.title,
        pastPassengerNumber: travellerForm.pastPassengerNumber,
        contactInfo: {
          emailAddress,
          phoneNumber,
          ...(travellerForm.address && {
            address: {
              address1: travellerForm.address,
              city: travellerForm.city,
              state: travellerForm.state,
              postalCode: travellerForm.postcode,
              country: travellerForm.countryOfResidence,
            },
          }),
        },
      },
    }
  })

  return traveller
}

export const formatVendorsOptions = (cruiseCabinPreferences: App.CruiseCabinPreferences) => ({
  ...(cruiseCabinPreferences?.diningOption && { dining: cruiseCabinPreferences.diningOption }),
  ...(cruiseCabinPreferences?.bedConfiguration && { bedConfiguration: cruiseCabinPreferences.bedConfiguration }),
  ...(cruiseCabinPreferences?.insurance && { insurance: cruiseCabinPreferences.insurance }),
  ...(cruiseCabinPreferences?.prepaidGratuity && { prepaidGratuities: cruiseCabinPreferences.prepaidGratuity }),
  ...(cruiseCabinPreferences?.tableSize && { tableSize: cruiseCabinPreferences.tableSize }),
})

export function offerHasValidDepartures(offer: App.CruiseOffer): boolean {
  return (offer.departureDates ?? []).filter(d => moment(new Date(d)) >= moment()).length > 0
}

type PricingSchedule = Partial<CruisesContract.PricingCancellationSchedule> & Partial<CruisesContract.PricingPaymentSchedule>
export function filterDataByHighestPercentage(data: PricingSchedule[], isNonRefundable: boolean) {
  const areAllDatesEqual = data.every(obj => obj.date === data[0].date)
  let filteredData = data

  if (areAllDatesEqual) {
    filteredData = data.filter(obj => obj.isPercentage)
  }

  const finalResult = {}

  filteredData.forEach(obj => {
    const date = obj.date as string
    const amount = obj.amount as number
    if (!(date in finalResult) || amount > finalResult[date].amount) {
      finalResult[date] = {
        ...obj,
        isNonRefundable: !!isNonRefundable,
      }
    }
  })

  const newArray: App.PricingCancellationSchedule[] = Object.values(finalResult)

  return newArray
}

function getDeparturePortSearchItem(queryParams: URLSearchParams): App.SearchItem | undefined {
  const id = queryParams.get('portId')
  const name = queryParams.get('portName')
  if (id && name) {
    return {
      searchType: 'destination',
      value: id,
      format: {
        mainText: name,
      },
    }
  }
}

export function getCruiseSearchStateFromParams(queryParams: URLSearchParams): Partial<GlobalSearchState> {
  const durationMin = queryParams.get('durationMin')
  const durationMax = queryParams.get('durationMax')

  return {
    searchItem: getSearchItemFromURLSearchParams(queryParams),
    location: queryParams.get('destinationName') || '',
    secondarySearchItem: getDeparturePortSearchItem(queryParams),
    secondaryLocation: queryParams.get('portName') || '',
    cruiseLines: queryParams.getAll('cruiseLines'),
    flexibleMonths: queryParams.get('departureMonths') ?? undefined,
    durationMin: durationMin ? parseInt(durationMin) : undefined,
    durationMax: durationMax ? parseInt(durationMax) : undefined,
  }
}
